import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import {deleteAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import RetryFailedSaveToResultsStepBanner from 'analysis-tool/structured-data-mapping/RetryFailedSaveToResultsStepBanner';
import useLogAnalysisProfilerTimingFinished from 'analysis-tool/ui/hooks/useLogAnalysisProfilerTimingFinished';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert} from 'Asserts';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SAVE_TO_RESULTS_FINISHED_EVENT} from 'performance/processAnalysesEvents';
import {DeletePipelineStepsBehavior} from 'pipelines/PipelineDAO';
import getTestProps from 'test/lib/cypress/getTestProps';

import DeleteFailedSaveToResultsStepBanner from './DeleteFailedSaveToResultsStepBanner';
import SucceededDataMappingResults from './SucceededDataMappingResults';

type SaveToResultsStepOutputViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT>,
  'analysis' | 'step' | 'clientStateId' | 'onOpenStepInitializationView'
>;

export default function SaveToResultsStepOutputView({
  analysis,
  step,
  clientStateId,
  onOpenStepInitializationView,
}: SaveToResultsStepOutputViewProps): JSX.Element {
  useLogAnalysisProfilerTimingFinished(
    step.statusCode === PipelineStepStatusCode.WAITING || step.statusCode === PipelineStepStatusCode.RUNNING,
    [SAVE_TO_RESULTS_FINISHED_EVENT]
  );

  const openReconfigurationView = (): void => {
    onOpenStepInitializationView({
      type: AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION,
      stepId: step.id,
    });
  };

  function getContents(): ReactNode {
    switch (step.statusCode) {
      case PipelineStepStatusCode.FAILED:
        assert(step.statusMessage != null, 'Expected statusMessage to be defined for FAILED step.');
        if (getDownstreamSteps(analysis, step.id).size > 0) {
          return (
            <DeleteFailedSaveToResultsStepBanner
              deleteStep={
                analysis.permissions.writable
                  ? () =>
                      deleteAnalysisSteps(analysis.id, {
                        stepIds: [step.id],
                        behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
                      })
                  : undefined
              }
              supportingInfo={step.statusMessage}
            />
          );
        } else {
          return (
            <RetryFailedSaveToResultsStepBanner
              openReconfigurationView={analysis.permissions.writable ? openReconfigurationView : undefined}
              supportingInfo={step.statusMessage}
            />
          );
        }
      case PipelineStepStatusCode.SUCCEEDED:
        return (
          <SucceededDataMappingResults
            analysis={analysis}
            step={step}
            clientStateId={clientStateId}
            openReconfigurationView={openReconfigurationView}
          />
        );
      default:
        throw new Error(`Unexpected step status for SAVE_TO_RESULTS: ${step.statusCode}`);
    }
  }

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'SaveToResultsStepOutputView', element: 'root'})}
    >
      {getContents()}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    margin: ANALYSIS_TOOL_SPACING,
  },
});
